import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { UtensilsCrossed, Leaf, Coffee, Sandwich, Wine, Sparkles, Clock, ChefHat, Armchair, LayoutGrid } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventTypes = [
  { value: "morning_coffee", label: "Coffee Break", icon: Coffee, hasServiceStyle: false },
  { value: "lunch", label: "Lunch", icon: Sandwich, hasServiceStyle: true },
  { value: "cocktail", label: "Cocktail Reception", icon: Wine, hasServiceStyle: false },
  { value: "gala_dinner", label: "Gala Dinner", icon: Sparkles, hasServiceStyle: true },
  { value: "full_day", label: "Full Day Conference", icon: Clock, hasServiceStyle: false },
];

const getItemIcon = (key) => {
  if (key.includes('coffee')) return '☕';
  if (key.includes('tea')) return '🍵';
  if (key.includes('water')) return '💧';
  if (key.includes('wine')) return '🍷';
  if (key.includes('beer')) return '🍺';
  if (key.includes('soft_drink')) return '🥤';
  if (key.includes('pastries') || key.includes('donuts')) return '🥐';
  if (key.includes('cookies') || key.includes('brownies') || key.includes('dessert')) return '🍪';
  if (key.includes('salad')) return '🥗';
  if (key.includes('fruit')) return '🍎';
  if (key.includes('bread') || key.includes('rolls')) return '🍞';
  if (key.includes('canapes_seafood')) return '🦐';
  if (key.includes('canapes')) return '🍢';
  if (key.includes('salty_snacks')) return '🥨';
  if (key.includes('appetizer') || key.includes('appetiser')) return '🥗';
  if (key.includes('main_protein') || key.includes('main_course')) return '🍖';
  if (key.includes('alternative_main')) return '🍝';
  if (key.includes('vegetarian') || key.includes('vegan')) return '🥬';
  if (key.includes('potatoes_rice')) return '🍚';
  if (key.includes('vegetable') || key.includes('starch')) return '🥦';
  if (key.includes('creamer') || key.includes('milk')) return '🥛';
  if (key.includes('sugar') || key.includes('sweetener')) return '🧂';
  return '🍽️';
};

export const CateringEstimator = () => {
  const [guestCount, setGuestCount] = useState("");
  const [eventType, setEventType] = useState("lunch");
  const [duration, setDuration] = useState("4");
  const [sustainabilityMode, setSustainabilityMode] = useState(false);
  const [isMetric, setIsMetric] = useState(true);
  const [serviceStyle, setServiceStyle] = useState("served");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Seated Buffet — Chair Planner state
  const [chairGuests, setChairGuests] = useState("");
  const [chairsPerTable, setChairsPerTable] = useState("10");
  const [fillRate, setFillRate] = useState(80);

  const navigate = useNavigate();

  const selectedEventType = eventTypes.find(t => t.value === eventType);
  const showServiceStyle = selectedEventType?.hasServiceStyle;

  const handleCalculate = async () => {
    if (!guestCount) {
      toast.error("Please enter guest count");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/calculate/catering`, {
        guest_count: parseInt(guestCount),
        event_type: eventType,
        duration_hours: parseFloat(duration),
        sustainability_mode: sustainabilityMode,
        is_metric: isMetric,
        service_style: serviceStyle
      });
      setResult(response.data);
      toast.success(sustainabilityMode ? "Sustainable estimate ready" : "Estimate calculated");
    } catch (error) {
      toast.error("Calculation failed");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout 
      title="Catering Estimator" 
      subtitle="Waste-Zero food & beverage calculator with sustainability mode"
    >
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Input Card */}
        <Card className="tactical-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg uppercase">
              <UtensilsCrossed className="h-5 w-5 text-cyan-400" />
              Event Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Toggles Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Sustainability Toggle */}
              <div className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/30 rounded-sm">
                <div className="flex items-center gap-3">
                  <Leaf className="h-5 w-5 text-green-400" />
                  <div>
                    <p className="text-sm font-semibold text-green-400">Sustainability Mode</p>
                    <p className="text-xs text-muted-foreground">10% reduction for no-shows</p>
                  </div>
                </div>
                <Switch
                  checked={sustainabilityMode}
                  onCheckedChange={setSustainabilityMode}
                  data-testid="sustainability-toggle"
                />
              </div>

              {/* Unit Toggle */}
              <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-sm">
                <span className="text-sm font-medium">Unit System</span>
                <div className="flex items-center gap-3">
                  <span className={`text-sm ${!isMetric ? 'text-foreground' : 'text-muted-foreground'}`}>
                    Imperial
                  </span>
                  <Switch
                    checked={isMetric}
                    onCheckedChange={setIsMetric}
                    data-testid="unit-toggle"
                  />
                  <span className={`text-sm ${isMetric ? 'text-foreground' : 'text-muted-foreground'}`}>
                    Metric
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="guests" className="label-tactical">Guest Count</Label>
                <Input
                  id="guests"
                  type="number"
                  placeholder="100"
                  value={guestCount}
                  onChange={(e) => setGuestCount(e.target.value)}
                  className="tactical-input"
                  data-testid="guest-count-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-tactical">Event Type</Label>
                <Select value={eventType} onValueChange={setEventType}>
                  <SelectTrigger className="tactical-input" data-testid="event-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {eventTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <type.icon className="h-4 w-4" />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="duration" className="label-tactical">Duration (hours)</Label>
                <Input
                  id="duration"
                  type="number"
                  placeholder="4"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="tactical-input"
                  data-testid="duration-input"
                />
              </div>
              {showServiceStyle && (
                <div className="space-y-2">
                  <Label className="label-tactical">Service Style</Label>
                  <Select value={serviceStyle} onValueChange={setServiceStyle}>
                    <SelectTrigger className="tactical-input" data-testid="service-style-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="served">
                        <div className="flex items-center gap-2">
                          <ChefHat className="h-4 w-4" />
                          Served
                        </div>
                      </SelectItem>
                      <SelectItem value="buffet">
                        <div className="flex items-center gap-2">
                          <UtensilsCrossed className="h-4 w-4" />
                          Buffet
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <Button 
              onClick={handleCalculate} 
              className="tactical-btn w-full bg-cyan-600 hover:bg-cyan-700"
              disabled={loading}
              data-testid="calculate-catering-button"
            >
              {loading ? "Calculating..." : "Calculate Quantities"}
            </Button>
          </CardContent>
        </Card>

        {/* Result Card */}
        {result && (
          <Card className="tactical-card border-cyan-500/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg uppercase text-cyan-400">
                  <UtensilsCrossed className="h-5 w-5" />
                  Estimated Quantities
                </CardTitle>
                {result.sustainability_mode && (
                  <span className="suite-badge border-green-500/50 bg-green-500/10 text-green-400">
                    <Leaf className="h-3 w-3 mr-1" />
                    Eco Mode
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4 p-3 bg-secondary/50 rounded-sm">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="label-tactical">Guests</p>
                    <p className="text-2xl font-mono font-bold">{result.guest_count}</p>
                  </div>
                  <div>
                    <p className="label-tactical">Event</p>
                    <p className="text-sm font-semibold capitalize">{result.event_type.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="label-tactical">Duration</p>
                    <p className="text-2xl font-mono font-bold">{result.duration_hours}h</p>
                  </div>
                  {result.service_style && (
                    <div>
                      <p className="label-tactical">Service</p>
                      <p className="text-sm font-semibold capitalize">{result.service_style}</p>
                    </div>
                  )}
                </div>
              </div>

              {result.savings_note && (
                <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-sm">
                  <p className="text-sm text-green-400 flex items-center gap-2">
                    <Leaf className="h-4 w-4" />
                    {result.savings_note}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {result.quantities.map((item) => (
                  <div
                    key={item.key}
                    className="p-3 bg-secondary/30 rounded-sm border border-border/30"
                    data-testid={`quantity-${item.key}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg" aria-hidden="true">{getItemIcon(item.key)}</span>
                      <span className="text-xs text-muted-foreground">
                        {item.label}
                      </span>
                    </div>
                    <p className="text-xl font-mono font-bold leading-tight">
                      {item.primary.value.toLocaleString()} <span className="text-sm font-normal text-muted-foreground">{item.primary.unit}</span>
                    </p>
                    {item.secondary && (
                      <p className="text-xs text-muted-foreground font-mono mt-0.5">
                        ({item.secondary.value.toLocaleString()} {item.secondary.unit})
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Seated Buffet - Chair Planner */}
        <Card className="tactical-card" data-testid="chair-planner-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg uppercase">
              <Armchair className="h-5 w-5 text-cyan-400" />
              Seated Buffet — Chair Planner
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Calculates total chairs &amp; tables needed. Accounts for the fact that guests rarely fill a table to capacity — adjust the comfort fill rate below to match your crowd.
            </p>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="chair-guests" className="label-tactical">Guest Count</Label>
                <Input
                  id="chair-guests"
                  type="number"
                  min="1"
                  placeholder={guestCount || "100"}
                  value={chairGuests}
                  onChange={(e) => setChairGuests(e.target.value)}
                  className="tactical-input"
                  data-testid="chair-guest-input"
                />
                {guestCount && !chairGuests && (
                  <button
                    type="button"
                    className="text-xs text-cyan-400 hover:underline"
                    onClick={() => setChairGuests(guestCount)}
                    data-testid="chair-use-above-button"
                  >
                    Use {guestCount} from above
                  </button>
                )}
              </div>

              <div className="space-y-2">
                <Label className="label-tactical">Chairs Per Table</Label>
                <Select value={chairsPerTable} onValueChange={setChairsPerTable}>
                  <SelectTrigger className="tactical-input" data-testid="chairs-per-table-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">6 (small round / sweetheart)</SelectItem>
                    <SelectItem value="8">8 (rectangular banquet)</SelectItem>
                    <SelectItem value="10">10 (standard round, 60&quot; / 152cm)</SelectItem>
                    <SelectItem value="12">12 (large round, 72&quot; / 183cm)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="label-tactical">Comfort Fill Rate</Label>
                  <span className="text-xs font-mono text-cyan-400">{fillRate}%</span>
                </div>
                <input
                  type="range"
                  min="50"
                  max="100"
                  step="5"
                  value={fillRate}
                  onChange={(e) => setFillRate(parseInt(e.target.value))}
                  className="w-full accent-cyan-500"
                  data-testid="fill-rate-slider"
                />
                <p className="text-[10px] text-muted-foreground leading-tight">
                  100% = packed • 80% = standard • 65% = generous spacing
                </p>
              </div>
            </div>

            {chairGuests && parseInt(chairGuests) > 0 && (() => {
              const guests = parseInt(chairGuests);
              const perTable = parseInt(chairsPerTable);
              const effectiveSeats = Math.max(1, Math.floor(perTable * (fillRate / 100)));
              const tablesNeeded = Math.ceil(guests / effectiveSeats);
              const totalChairs = tablesNeeded * perTable;
              const emptyChairs = totalChairs - guests;
              return (
                <div className="border-t border-border/40 pt-5">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="chair-planner-result">
                    <div className="p-4 bg-secondary/30 rounded-sm border border-border/30">
                      <p className="label-tactical">Tables</p>
                      <p className="text-3xl font-mono font-bold text-cyan-400" data-testid="result-tables">{tablesNeeded}</p>
                      <p className="text-xs text-muted-foreground mt-1">@ {perTable} chairs each</p>
                    </div>
                    <div className="p-4 bg-secondary/30 rounded-sm border border-border/30">
                      <p className="label-tactical">Total Chairs</p>
                      <p className="text-3xl font-mono font-bold text-cyan-400" data-testid="result-chairs">{totalChairs}</p>
                      <p className="text-xs text-muted-foreground mt-1">to set out</p>
                    </div>
                    <div className="p-4 bg-secondary/30 rounded-sm border border-border/30">
                      <p className="label-tactical">Effective Seats / Table</p>
                      <p className="text-3xl font-mono font-bold">{effectiveSeats}</p>
                      <p className="text-xs text-muted-foreground mt-1">at {fillRate}% fill</p>
                    </div>
                    <div className="p-4 bg-secondary/30 rounded-sm border border-border/30">
                      <p className="label-tactical">Empty Chairs</p>
                      <p className="text-3xl font-mono font-bold text-muted-foreground" data-testid="result-empty">{emptyChairs}</p>
                      <p className="text-xs text-muted-foreground mt-1">buffer / no-shows</p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">
                    <p className="text-xs text-muted-foreground italic">
                      Tip: lower the fill rate when guests arrive in small groups (couples, pairs) — they&apos;ll naturally leave gaps.
                    </p>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/planning/floor-plan?tables=${tablesNeeded}&chairs=${perTable}`)}
                      className="bg-yellow-600 hover:bg-yellow-700"
                      data-testid="send-to-floor-plan-button"
                    >
                      <LayoutGrid className="h-4 w-4 mr-2" />
                      Send to Floor Plan
                    </Button>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Event Type Guide */}
        <Card className="tactical-card">
          <CardHeader>
            <CardTitle className="text-lg uppercase">Event Type Guide</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {eventTypes.map((type) => (
                <div 
                  key={type.value}
                  className={`p-3 rounded-sm border transition-colors cursor-pointer ${
                    eventType === type.value 
                      ? 'border-cyan-500/50 bg-cyan-500/10' 
                      : 'border-border/50 hover:border-border'
                  }`}
                  onClick={() => setEventType(type.value)}
                >
                  <type.icon className={`h-6 w-6 mb-2 ${eventType === type.value ? 'text-cyan-400' : 'text-muted-foreground'}`} />
                  <p className="text-sm font-semibold">{type.label}</p>
                  {type.hasServiceStyle && (
                    <p className="text-xs text-muted-foreground mt-1">Buffet or Served</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default CateringEstimator;
